import { useState, useEffect, useCallback } from 'react';
import {
  findElementBySelectors,
  findTabElement,
} from '../utils/dom';
import { useOnboarding } from '../../../context/OnboardingContext';

/**
 * Хук для управления отображением и взаимодействием с шагом онбординга
 *
 * @param {Object} stepData - данные о текущем шаге
 * @param {Boolean} isActive - активен ли онбординг
 * @param {Function} onComplete - функция, вызываемая при выполнении условия продолжения
 * @returns {Object} - объект с данными для отображения шага
 */
export const useOnboardingStep = (stepData, isActive, onComplete) => {
  const { pageRef } = useOnboarding();
  const [targetElement, setTargetElement] = useState(null);
  const [isModalStep, setIsModalStep] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState('bottom');
  const [isAwaitingAction, setIsAwaitingAction] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Функция для управления состоянием дровера
  const handleDrawerState = useCallback((step) => {
    if (step?.requiredUIState?.drawerOpen) {
      // Проверяем, в мобильном ли мы режиме
      const isMobileView = window.innerWidth < 600; // Соответствует MUI sm breakpoint

      if (isMobileView) {
        console.log(
          'handleDrawerState: требуется открыть дровер для шага',
          step.order
        );

        // Пробуем найти кнопку разными способами
        const drawerButton =
          document.querySelector('[data-testid="drawer-toggle"]') ||
          document.querySelector('[aria-label="open drawer"]');

        if (drawerButton) {
          console.log('Найдена кнопка открытия дровера, кликаем');
          // Принудительно вызываем клик для открытия дровера
          drawerButton.click();
          setDrawerOpen(true);

          // Устанавливаем таймаут на повторную проверку и клик
          setTimeout(() => {
            const isDrawerVisible = document.querySelector('.MuiDrawer-paper');
            if (
              !isDrawerVisible ||
              !isDrawerVisible.getBoundingClientRect().width
            ) {
              console.log('Повторный клик по кнопке дровера');
              drawerButton.click();
            }
          }, 300);
        } else {
          console.warn('Кнопка открытия дровера не найдена');
        }
      }
    }
  }, []);

  // Новая функция для определения правильного дровера для таргетирования
  const getCorrectDrawer = useCallback(() => {
    const isMobileView = window.innerWidth < 600; // Соответствует MUI sm breakpoint

    if (isMobileView) {
      // На мобильных устройствах ищем временный дровер
      return (
        document.querySelector(
          '[data-drawer-type="mobile"] .MuiDrawer-paper'
        ) || document.querySelector('.MuiDrawer-paper')
      );
    } else {
      // На десктопе ищем постоянный дровер
      return (
        document.querySelector(
          '[data-drawer-type="desktop"] .MuiDrawer-paper'
        ) || document.querySelector('.MuiDrawer-paper')
      );
    }
  }, []);

  // Функция для получения элемента nav, используемого в шаге онбординга
  const getNavElement = useCallback(() => {
    // Сначала ищем по классу
    const navElement = document.querySelector('.main-navigation');
    if (navElement) return navElement;

    // Затем ищем стандартный nav элемент
    return document.querySelector('nav');
  }, []);

  // Проверка условий отображения
  const checkDisplayConditions = useCallback(
    (step) => {
      if (!step || !step.displayConditions) return true;

      // Проверка требований к UI
      if (step.requiredUIState?.drawerOpen) {
        handleDrawerState(step);
      }

      // Проверка пользовательского условия
      if (step.displayConditions.customCondition) {
        try {
          // eslint-disable-next-line no-new-func
          const condition = new Function(
            `return ${step.displayConditions.customCondition}`
          )();
          if (!condition) return false;
        } catch (error) {
          console.error(
            'Ошибка при проверке пользовательского условия отображения:',
            error
          );
          return false;
        }
      }

      return true;
    },
    [handleDrawerState]
  );

  // Настройка обработчика для условий продолжения
  const setupContinuationHandler = useCallback((step, callback) => {
    if (
      !step ||
      !step.continuationConditions ||
      !step.continuationConditions.enabled
    ) {
      return null;
    }

    const {
      eventType,
      elementSelector,
    } = step.continuationConditions;

    // Создаем переменную для защиты от множественных вызовов
    let isCallbackTriggered = false;

    // Функция для безопасного вызова callback
    const safeCallback = () => {
      if (isCallbackTriggered) {
        console.log(
          '⚠️ Повторный вызов колбэка continuationConditions предотвращен'
        );
        return;
      }

      // Устанавливаем флаг, чтобы предотвратить повторные вызовы
      isCallbackTriggered = true;

      console.log(
        `✅ Условие продолжения выполнено: [${eventType}] на ${elementSelector}`
      );
      callback();
    };

    // Определяем тип события (клик по умолчанию)
    const eventName = eventType || 'click';

    // Получаем элемент(ы) по селектору
    let elements = [];
    try {
      elements = document.querySelectorAll(elementSelector);
    } catch (error) {
      console.error('Ошибка при поиске элемента:', error);
    }

    if (elements.length === 0) {
      console.warn(
        `Элемент не найден для условия продолжения: ${elementSelector}`
      );

      // Для обработки некоторых специальных случаев, например, когда элемент
      // может появиться позже (табы, подгружаемые элементы и т.д.)
      const observer = new MutationObserver((mutations) => {
        const newElements = document.querySelectorAll(elementSelector);
        if (newElements.length > 0) {
          console.log(
            `🔍 Найден элемент ${elementSelector} после изменения DOM`
          );
          newElements.forEach((el) => {
            el.addEventListener(eventName, safeCallback);
          });
          // Останавливаем наблюдение после нахождения элементов
          observer.disconnect();
        }
      });

      // Начинаем наблюдение за изменениями DOM
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Возвращаем функцию очистки
      return () => {
        observer.disconnect();
        document.querySelectorAll(elementSelector).forEach((el) => {
          el.removeEventListener(eventName, safeCallback);
        });
      };
    }

    // Добавляем обработчик события ко всем найденным элементам
    elements.forEach((element) => {
      element.addEventListener(eventName, safeCallback);
    });

    // Возвращаем функцию для удаления обработчика
    return () => {
      elements.forEach((element) => {
        element.removeEventListener(eventName, safeCallback);
      });
    };
  }, []);

  // Улучшенная функция для скроллинга к элементу с учетом его местоположения
  const scrollToElement = useCallback((element) => {
    if (!element) return;

    // Ищем родительский дровер (более надежный подход)
    let drawerElement = null;
    let currentElement = element;

    while (currentElement && currentElement !== document.body) {
      if (
        currentElement.classList &&
        (currentElement.classList.contains('MuiDrawer-paper') ||
          currentElement.getAttribute('data-drawer-content'))
      ) {
        drawerElement = currentElement;
        break;
      }
      currentElement = currentElement.parentElement;
    }

    // Если элемент находится внутри дровера
    if (drawerElement) {
      console.log('Скроллинг внутри дровера для элемента:', element);

      // Получаем прямоугольники дровера и элемента
      const drawerRect = drawerElement.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Проверяем видимость элемента внутри дровера
      const isVisible =
        elementRect.top >= drawerRect.top &&
        elementRect.bottom <= drawerRect.bottom;

      if (!isVisible) {
        // Вычисляем относительное положение элемента внутри дровера
        const relativeTop = elementRect.top - drawerRect.top;

        // Если элемент выше видимой области, скроллим к нему с отступом сверху
        if (elementRect.top < drawerRect.top) {
          drawerElement.scrollTop = drawerElement.scrollTop + relativeTop - 20;
        }
        // Если элемент ниже видимой области, скроллим с учетом высоты элемента
        else if (elementRect.bottom > drawerRect.bottom) {
          drawerElement.scrollTop =
            drawerElement.scrollTop +
            relativeTop -
            drawerRect.height +
            elementRect.height +
            20;
        }
        // Для списков меню используем более точное позиционирование
        else if (
          element.closest('.sidebar-menu') ||
          element.classList.contains('sidebar-menu')
        ) {
          // Центрируем элемент в дровере
          drawerElement.scrollTop =
            drawerElement.scrollTop +
            relativeTop -
            drawerRect.height / 2 +
            elementRect.height / 2;
        }

        console.log('Прокрутка дровера:', {
          scrollTop: drawerElement.scrollTop,
          relativeTop,
          drawerHeight: drawerRect.height,
          elementHeight: elementRect.height,
        });
      }
    } else {
      // Для элементов вне дровера используем стандартный скроллинг
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  // Эффект для обновления данных шага
  useEffect(() => {
    if (!isActive || !stepData) {
      setTargetElement(null);
      setIsModalStep(false);
      return;
    }

    // Проверяем условия отображения
    if (!checkDisplayConditions(stepData)) {
      // Если условия не выполнены, сразу вызываем callback для перехода к следующему шагу
      onComplete && onComplete();
      return;
    }

    // Функция для поиска элемента с повторными попытками
    const findElement = () => {
      // Пустой селектор или 'body' означает модальный шаг
      if (
        !stepData.selector ||
        stepData.selector === 'body' ||
        stepData.selector === ''
      ) {
        setIsModalStep(true);
        setTargetElement(document.body);
        return;
      }

      // Добавляем небольшую задержку для дровера
      const searchWithDelay = () => {
        // Если шаг требует открытого дровера, даем больше времени на его открытие
        const delayTime = stepData.requiredUIState?.drawerOpen ? 500 : 100;

        setTimeout(() => {
          // Особый случай: если селектор относится к дроверу или его элементам
          if (
            stepData.selector === 'nav' ||
            stepData.selector.includes('.MuiDrawer-paper') ||
            stepData.selector.includes('sidebar-menu') ||
            stepData.selector.includes('theme-switch') ||
            stepData.selector.includes('profile') ||
            stepData.selector.includes('notifications')
          ) {
            console.log('Поиск элемента в дровере:', stepData.selector);

            // Получаем правильный дровер в зависимости от вида устройства
            const drawerElement = getCorrectDrawer();

            if (drawerElement) {
              // Ищем элемент внутри дровера
              let target = null;

              if (stepData.selector === 'nav') {
                // Если селектор 'nav', используем компонент nav
                target = getNavElement() || drawerElement;
                console.log('Используем компонент nav для селектора nav');
              } else if (stepData.selector === '.MuiDrawer-paper') {
                // Непосредственно сам дровер
                target = drawerElement;
                console.log('Используем сам элемент дровера');
              } else if (stepData.selector === '.sidebar-menu') {
                target = drawerElement.querySelector('.sidebar-menu');
              } else if (stepData.selector === '.theme-switch') {
                target = drawerElement.querySelector('.theme-switch');
              } else if (stepData.selector === '.profile') {
                target = drawerElement.querySelector('.profile');
              } else if (stepData.selector === '.notifications') {
                target = drawerElement.querySelector('.notifications');
              } else {
                // Пытаемся найти по обычному селектору внутри дровера
                target = drawerElement.querySelector(stepData.selector);
              }

              if (target) {
                console.log('Найден элемент в дровере:', target);
                setTargetElement(target);
                setIsModalStep(false);

                // Устанавливаем положение и размещение тултипа
                const rect = target.getBoundingClientRect();
                setPosition({
                  top: rect.top + window.scrollY,
                  left: rect.left + window.scrollX,
                  width: rect.width,
                  height: rect.height,
                });

                // Используем правильный placement в зависимости от селектора
                if (stepData.selector === 'nav') {
                  setPlacement('right');
                } else {
                  setPlacement(stepData.placement || 'right');
                }
                return;
              } else {
                console.warn(
                  `Элемент ${stepData.selector} не найден в дровере`
                );
              }
            } else {
              console.warn('Дровер не найден для текущего размера экрана');
            }
          }

          // Проверяем, является ли селектор для вкладки
          if (
            stepData.selector.includes('[role="tab"]') ||
            (stepData.tabName && stepData.tabName.length > 0)
          ) {
            console.log(
              'Поиск вкладки:',
              stepData.selector || stepData.tabName
            );

            // Проверяем наличие data-tab-id в селекторе
            const tabIdMatch =
              stepData.selector &&
              stepData.selector.match(/\[data-tab-id="([^"]+)"\]/);
            if (tabIdMatch && tabIdMatch[1]) {
              const tabId = tabIdMatch[1];
              console.log(`Обнаружен data-tab-id="${tabId}" в селекторе`);

              // Используем ID вкладки для поиска
              const tabElement = findTabElement(tabId);
              if (tabElement) {
                console.log('Найдена вкладка по data-tab-id:', tabElement);
                setTargetElement(tabElement);
                setIsModalStep(false);

                // Устанавливаем положение и размещение тултипа
                const rect = tabElement.getBoundingClientRect();
                setPosition({
                  top: rect.top + window.scrollY,
                  left: rect.left + window.scrollX,
                  width: rect.width,
                  height: rect.height,
                });
                setPlacement(stepData.placement || 'bottom');

                // Прокручиваем к вкладке с задержкой для надежности
                setTimeout(() => {
                  scrollToElement(tabElement);
                }, 150);
                return;
              }
            }

            // Если используется tabName, пробуем найти вкладку по этому имени
            if (stepData.tabName) {
              const tab = document.querySelector(
                `[role="tab"][data-tab-id="${stepData.tabName}"]`
              );
              if (tab) {
                console.log('Найдена вкладка по tabName:', tab);
                setTargetElement(tab);
                setIsModalStep(false);
                return;
              }
            }

            // Если data-tab-id не найден или вкладка не найдена, используем обычный поиск
            const tab = findTabElement(stepData.selector);

            if (tab) {
              console.log('Найдена вкладка:', tab);
              setTargetElement(tab);
              setIsModalStep(false);

              // Устанавливаем положение и размещение тултипа
              const rect = tab.getBoundingClientRect();
              setPosition({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                height: rect.height,
              });
              setPlacement(stepData.placement || 'bottom');

              // Прокручиваем к вкладке с задержкой для надежности
              setTimeout(() => {
                scrollToElement(tab);
              }, 150);
              return;
            }
          }

          // Стандартный поиск элемента
          let target = null;

          try {
            target = findElementBySelectors(stepData.selector);
          } catch (error) {
            console.warn('Ошибка при поиске элемента:', error);
          }

          if (target) {
            setTargetElement(target);
            setIsModalStep(false);

            // Устанавливаем положение и размещение тултипа
            const rect = target.getBoundingClientRect();
            setPosition({
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
              height: rect.height,
            });
            setPlacement(stepData.placement || 'bottom');

            // Прокручиваем страницу к элементу, если он не виден
            if (
              rect.top < 0 ||
              rect.left < 0 ||
              rect.bottom > window.innerHeight ||
              rect.right > window.innerWidth
            ) {
              // Добавляем задержку для более надежного скроллинга
              setTimeout(() => {
                scrollToElement(target);
              }, 150);
            }
          } else {
            console.warn(
              `Элемент с селектором "${stepData.selector}" не найден для шага онбординга. Используем модальный режим.`
            );

            // Используем модальный шаг вместо пропуска этого шага
            setIsModalStep(true);
            setTargetElement(document.body);

            // При нахождении элемента в будущем переключаемся на него
            const observer = new MutationObserver(() => {
              const newTarget = findElementBySelectors(stepData.selector);
              if (newTarget) {
                setTargetElement(newTarget);
                setIsModalStep(false);
                observer.disconnect();
              }
            });

            // Наблюдаем за изменениями DOM
            observer.observe(document.body, {
              childList: true,
              subtree: true,
              attributes: true,
            });

            // Остановим наблюдение через 5 секунд, если элемент так и не найден
            setTimeout(() => observer.disconnect(), 5000);
          }
        }, delayTime);
      };

      // Запускаем поиск с задержкой
      searchWithDelay();
    };

    // Вызываем поиск элемента
    findElement();

    // Настраиваем обработчик для условий продолжения
    const cleanupHandler = setupContinuationHandler(stepData, onComplete);

    // --- Новый блок: автоматическое переключение таба, если нужно ---
    if (isActive && stepData && stepData.tabName) {
      if (pageRef?.current?.setTab) {
        pageRef.current.setTab(stepData.tabName);
      } else {
        setTimeout(() => {
          if (pageRef?.current?.setTab) {
            pageRef.current.setTab(stepData.tabName);
          }
        }, 300);
      }
    }

    return () => {
      if (cleanupHandler) cleanupHandler();
    };
  }, [
    isActive,
    stepData,
    pageRef,
    checkDisplayConditions,
    setupContinuationHandler,
    onComplete,
    getCorrectDrawer,
    getNavElement,
    scrollToElement,
  ]);

  // Добавим дополнительную логику для обработки элементов в списке меню
  useEffect(() => {
    if (isActive && stepData && targetElement) {
      // Проверяем, находится ли элемент в дровере
      if (
        stepData.selector === '.sidebar-menu' ||
        stepData.selector === '.profile' ||
        stepData.selector === '.theme-switch' ||
        stepData.selector.includes('ListItem')
      ) {
        console.log(
          'Принудительный скроллинг для элемента меню:',
          stepData.selector
        );

        // Добавляем небольшую задержку для корректного скроллинга
        setTimeout(() => {
          scrollToElement(targetElement);
        }, 200);
      } else {
        // Для всех остальных элементов в дровере тоже выполняем скроллинг
        // с небольшой задержкой для гарантии видимости
        let drawerElement = null;
        let currentElement = targetElement;

        // Проверяем, находится ли элемент в дровере
        while (currentElement && currentElement !== document.body) {
          if (
            currentElement.classList &&
            (currentElement.classList.contains('MuiDrawer-paper') ||
              currentElement.getAttribute('data-drawer-content'))
          ) {
            drawerElement = currentElement;
            break;
          }
          currentElement = currentElement.parentElement;
        }

        if (drawerElement) {
          console.log(
            'Элемент находится в дровере, выполняем дополнительный скроллинг'
          );
          setTimeout(() => {
            scrollToElement(targetElement);
          }, 300);
        }
      }
    }
  }, [isActive, stepData, targetElement, scrollToElement]);

  return {
    targetElement,
    isModalStep,
    position,
    placement,
    isAwaitingAction,
    continuationMessage:
      stepData?.continuationConditions?.message ||
      'Выполните действие для продолжения',
    setupContinuationHandler,
  };
};

// Функция для поиска вкладки по data-tab-id
export const findTabByDataId = (tabId) => {
  if (!tabId) return null;

  // Ищем все вкладки с атрибутом data-tab-id
  const tabs = document.querySelectorAll('[role="tab"][data-tab-id]');

  // Перебираем вкладки и ищем нужную
  for (let i = 0; i < tabs.length; i++) {
    if (tabs[i].getAttribute('data-tab-id') === tabId) {
      return {
        element: tabs[i],
        index: i,
      };
    }
  }

  return null;
};

// Функция для получения идентификатора таба из селектора
export const getTabIdFromSelector = (selector) => {
  if (!selector) return null;

  // Извлекаем data-tab-id из селектора
  const dataTabIdMatch = selector.match(/\[data-tab-id="([^"]+)"\]/);
  if (dataTabIdMatch && dataTabIdMatch[1]) {
    return dataTabIdMatch[1];
  }

  // Извлекаем индекс из nth-child
  const nthChildMatch = selector.match(/nth-child\((\d+)\)/);
  if (nthChildMatch && nthChildMatch[1]) {
    return parseInt(nthChildMatch[1]) - 1;
  }

  return null;
};
